import React, { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import Button from 'components/button'
import api from 'helpers/api'
import Modal from 'components/modal'
import { useAuth } from 'hooks/auth'
import { useApplicationStore } from 'hooks/application'
import { INTEGRATIONS } from 'routes'
import { IIntegrationRedirects, IIntegrationCatalogue } from 'types'
import IntegrationConnection from 'views/integrations/components/IntegrationConnection'
import ApiKeyConnection from 'views/integrations/components/ApiKeyConnection'
import ReviztoConnection from 'views/integrations/components/ReviztoConnection'
import KeyModal from './components/keyModal'
import { validateSourceUrl } from 'helpers/utils'
import { isAccountConnected, readReviztoRegion } from 'helpers/integration'
import { IntegrationProviders } from 'app-constants'
import Skeleton from 'components/skeleton'

const Integrations: React.FC = () => {
  const [newKeyModalOpen, setNewKeyModalOpen] = useState<boolean>(false)
  const [editKeyModalOpen, setEditKeyModalOpen] = useState<boolean>(false)
  const [deleteKeyDialogOpen, setDeleteKeyDialogOpen] = useState<boolean>(false)
  const [selectedAPIKeyPublicId, setSelectedAPIKeyPublicId] = useState<string>('')
  const [integrationRedirects, setIntegrationRedirects] = useState<IIntegrationRedirects>({})

  const [loadingCatalogue, setLoadingCatalogue] = useState<boolean>(true)
  const [catalogue, setCatalogue] = useState<IIntegrationCatalogue>({})

  const [connectingAccount, setConnectingAccount] = useState<boolean>(false)

  const { user, updateUser } = useAuth()

  const selectedAPIKey = user?.apiKeys
    .map((key) => {
      return {
        ...key,
        accessLevel: key.accessLevel === undefined ? 1 : key.accessLevel,
        siteRestrictions: key.siteRestrictions === undefined ? [] : key.siteRestrictions,
        projectRestrictions: key.projectRestrictions === undefined ? [] : key.projectRestrictions,
        tableRestrictions: key.tableRestrictions === undefined ? [] : key.tableRestrictions,
        documentRestrictions: key.documentRestrictions === undefined ? [] : key.documentRestrictions
      }
    })
    .find((apiKey) => apiKey.publicId === selectedAPIKeyPublicId)

  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const VIEWPOINT_ISS = 'https://identity-uk.team.viewpoint.com'

  // The callback URL holds a sign-in code, so it is replaced rather than kept in
  // history: pressing Back must not send the code again.
  const leaveCallback = () => navigate(INTEGRATIONS, { replace: true })

  const connectAccount = (provider: string, authorizationCode: string, region?: string) => {
    if (!user) return
    // Already connected (e.g. Back to the provider, which signs straight in again).
    if (isAccountConnected(user, provider)) {
      leaveCallback()
      return
    }
    setConnectingAccount(true)
    api
      .getAccessToken(provider, authorizationCode, region)
      .then(async (userResponse) => {
        await updateUser(userResponse.data)
        setSnackbarMessage({ status: 'success', message: `Successfully connected your ${provider} account.` })
        setConnectingAccount(false)
        leaveCallback()
      })
      .catch((e) => {
        displayErrorMessage(e)
        setConnectingAccount(false)
        leaveCallback()
      })
  }

  useEffect(() => {
    document.title = 'Integrations'
  }, [])

  useEffect(() => {
    // A provider that refuses or fails the sign-in sends the user back with an
    // OAuth error instead of a code; say so rather than landing silently.
    const error = searchParams.get('error')
    if (error) {
      setSnackbarMessage({
        status: 'error',
        message: `Your account was not connected: ${searchParams.get('error_description') || error}`
      })
      leaveCallback()
      return
    }

    const code = searchParams.get('code')
    if (!code) return

    const provider =
      searchParams.get('integration') ||
      searchParams.get('state') ||
      (searchParams.get('iss') === VIEWPOINT_ISS && 'viewpoint')

    // Revizto's token exchange needs the region the user signed in on.
    const region = provider === IntegrationProviders.REVIZTO ? readReviztoRegion() : undefined
    if (provider) connectAccount(provider, code, region)
  }, [searchParams])

  useEffect(() => {
    setLoadingCatalogue(true)
    // Refresh user data to ensure integration connection status is up to date
    api
      .getMe()
      .then((res) => updateUser(res.data))
      .catch(() => {
        // Ignore errors - this is a best-effort refresh
      })
    api
      .getIntegrationRedirects()
      .then((response) => {
        setIntegrationRedirects(response.data)
        return api.getIntegrationCatalogue()
      })
      .then((response) => {
        setCatalogue(response.data)
      })
      .catch((error) => {
        displayErrorMessage(error)
      })
      .finally(() => {
        setLoadingCatalogue(false)
      })
  }, [])

  const disconnectAccount = (provider: string) => {
    if (user && isAccountConnected(user, provider)) {
      setConnectingAccount(true)
      api
        .disconnectAccount(provider)
        .then(async (user) => {
          await updateUser(user.data)
          setSnackbarMessage({ status: 'success', message: `Successfully disconnected your ${provider} account.` })
          setConnectingAccount(false)
        })
        .catch(async () => {
          setSnackbarMessage({
            status: 'error',
            message: `Your ${provider} account couldn't be disconnected. Try again.`,
            offerSupport: true
          })
          // Refresh user data to sync frontend state with backend
          try {
            const res = await api.getMe()
            await updateUser(res.data)
          } catch {
            // Ignore refresh errors
          }
          setConnectingAccount(false)
        })
    }
  }

  const deleteAPIKey = async () => {
    if (user && selectedAPIKey) {
      try {
        await api.deleteApiKey(selectedAPIKey)
        const updateUserResponse = await api.getMe()
        await updateUser(updateUserResponse.data)
      } catch (error) {
        displayErrorMessage(error)
      }

      setDeleteKeyDialogOpen(false)
    }
  }

  return (
    <div className="w-full py-0 px-[30px]">
      <div className="flex flex-row items-center w-full text-lg font-bold mt-2.5 mb-8 py-2.5 border-b-2 border-divider">
        <div data-tour="api-keys-section">API Keys</div>
        <div className="flex items-center ml-auto">
          <Button to="https://docs.morta.io" internalType="secondary">
            API Docs
          </Button>
          <Button
            className="ml-[7px]"
            data-cy="new-api-key"
            data-tour="new-api-key"
            onClick={() => setNewKeyModalOpen(true)}
          >
            New API Key
          </Button>
        </div>
      </div>

      {!user ||
        !user.apiKeys ||
        (user.apiKeys.length === 0 && (
          <div>
            <div>You have no API keys</div>
          </div>
        ))}

      {user &&
        user.apiKeys &&
        user.apiKeys.length > 0 &&
        user.apiKeys.map(function (apiKey: any) {
          return (
            <div
              key={apiKey.publicId}
              className="flex flex-row items-center rounded shadow-heavy w-full p-2.5 flex-wrap my-5 border border-divider-strong"
            >
              <div>
                <span className="font-bold">{apiKey.name}</span> (starts with {apiKey.prefix})
              </div>
              <Button
                className="mr-2.5 ml-auto max-w-[150px]"
                onClick={() => {
                  setEditKeyModalOpen(true)
                  setSelectedAPIKeyPublicId(apiKey.publicId)
                }}
                data-cy="edit-api-key"
              >
                Edit
              </Button>
              <Button
                className="mr-2.5 max-w-[150px]"
                internalType="danger"
                onClick={() => {
                  setDeleteKeyDialogOpen(true)
                  setSelectedAPIKeyPublicId(apiKey.publicId)
                }}
                data-cy="delete-api-key"
              >
                Delete
              </Button>
            </div>
          )
        })}

      {user && newKeyModalOpen && (
        <KeyModal
          id="new-api-key-modal"
          open={newKeyModalOpen}
          setOpen={setNewKeyModalOpen}
          user={user}
          setUserData={updateUser}
          mode="new"
          initialApiKey={{
            name: 'New API Key',
            accessLevel: 1,
            siteRestrictions: [],
            projectRestrictions: [],
            tableRestrictions: [],
            documentRestrictions: [],
            hash: '',
            prefix: '',
            publicId: ''
          }}
        />
      )}

      {user && user.apiKeys && selectedAPIKey && (
        <KeyModal
          id="edit-api-key-modal"
          open={editKeyModalOpen}
          setOpen={setEditKeyModalOpen}
          onClose={() => {
            setSelectedAPIKeyPublicId('')
          }}
          user={user}
          setUserData={updateUser}
          initialApiKey={selectedAPIKey}
          mode="edit"
        />
      )}

      {user && user.apiKeys && selectedAPIKey && (
        <Modal
          id="delete-api-key-modal"
          open={deleteKeyDialogOpen}
          setOpen={setDeleteKeyDialogOpen}
          title="Delete API Key"
        >
          Once you have deleted this API key it cannot be restored
          <div className="flex items-center justify-end mt-5">
            <Button internalType="danger" onClick={() => setDeleteKeyDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              className="ml-2.5"
              internalType="accept"
              onClick={() => deleteAPIKey()}
              data-cy="confirm-delete-api-key"
            >
              Confirm
            </Button>
          </div>
        </Modal>
      )}

      <div className="flex flex-row items-center w-full text-lg font-bold mb-5 py-2.5 border-b border-divider mt-[50px]">
        <div data-tour="connected-accounts-section">Connected Accounts</div>
      </div>
      <p className="mb-4 rounded-lg border border-alert bg-really-light-yellow px-3 py-2 text-sm text-orange">
        Please log out of the respective platform in your browser before connecting your account.
      </p>

      {loadingCatalogue && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {[...Array(8)].map((_, index) => (
            <Skeleton key={index} className="rounded-lg h-[200px]" />
          ))}
        </div>
      )}

      <div data-tour="integration-connect-account" className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {!loadingCatalogue &&
          Object.keys(catalogue).map((provider) => {
            const providerCfg = catalogue[provider]
            if (!providerCfg) return null

            // API-key providers
            if (providerCfg.authType === 'api_key' && providerCfg.apiKeyConfig) {
              return (
                <ApiKeyConnection
                  key={provider}
                  provider={provider}
                  connected={isAccountConnected(user, provider)}
                  connectingAccount={connectingAccount}
                  apiKeyConfig={providerCfg.apiKeyConfig}
                  disconnectAccount={disconnectAccount}
                  logo={validateSourceUrl(`/assets/images/${provider}.webp`)}
                />
              )
            }

            // Revizto special case (region select, then OAuth 2.0 sign-in)
            if (provider === IntegrationProviders.REVIZTO && integrationRedirects?.revizto) {
              return (
                <ReviztoConnection
                  key={provider}
                  connected={isAccountConnected(user, provider)}
                  connectingAccount={connectingAccount}
                  disconnectAccount={disconnectAccount}
                  redirectUrl={integrationRedirects.revizto}
                />
              )
            }

            // Standard OAuth providers
            if (integrationRedirects?.[provider]) {
              return (
                <IntegrationConnection
                  key={provider}
                  provider={provider}
                  connected={isAccountConnected(user, provider)}
                  redirectUrl={integrationRedirects[provider]!}
                  connectingAccount={connectingAccount}
                  disconnectAccount={disconnectAccount}
                  logo={validateSourceUrl(`/assets/images/${provider}.webp`)}
                />
              )
            }

            return null
          })}
      </div>
    </div>
  )
}

export default Integrations
